import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { copy } from '~/_shared/utils/collections/collections';
import {
  createColorWithOpacity, guaranteeHash,
} from '../../_shared/components/colorPicker/colorPicker.helpers';
import { type ColorResult } from '../../_shared/components/colorPicker/colorPicker.types';
import { isNotFoundApiError } from '../../_shared/utils/api/apiError.helpers';
import { useTranslation } from '../../_shared/utils/hooks';
import { AppErrorType } from '../../appError/appErrorType.enum';
import { type ModalProps } from '../../modal/modalType.enum';
import { getBoundaryStyle } from '../../sidebar/sidebarApps/mapTools/boundary/boundaryItem.helpers';
import { useBoundaryUpdate } from '../../sidebar/sidebarApps/mapTools/boundary/hooks/useBoundaryUpdate';
import { BoundaryDeleteResult } from '../../store/boundaries/boundaries.repository';
import { type ExtendedBoundaryIdentifier } from '../../store/boundaries/boundaryIdentifier.type';
import { useBoundaryGroupPermissions } from '../../store/boundaryGroups/boundaryGroups.selectors';
import { boundaryItemRemoveSuccess } from '../../store/boundaryItems/boundaryItems.actionCreators';
import { useMapBoundariesSelector } from '../../store/boundaryItems/boundaryItems.selectors';
import { boundaryTerritoryGroupsUpdateRequest } from '../../store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  useBoundaryTerritoryGroupsIsLoadingSelector, useGetBoundaryTerritoryGroup,
} from '../../store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import { createAppError } from '../../store/modal/modal.actionCreators';
import { BoundaryItemEditSettingsComponent } from './boundaryItemEditSettings.component';

export type BoundaryItemSettingsProps = ModalProps<ExtendedBoundaryIdentifier>;

export const BoundaryItemSettingsContainer: FC<BoundaryItemSettingsProps> = (props) => {
  const boundaryItems = useMapBoundariesSelector();
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const { updateBoundary, isLoading: isBoundaryNameSaveLoading } = useBoundaryUpdate();
  const groupBoundaries = boundaryItems.get(props.boundaryGroupId);
  const boundary = groupBoundaries?.get(props.boundaryId);
  const { getBoundaryTerritoryGroupById } = useGetBoundaryTerritoryGroup();
  const boundaryTerritoryGroup = getBoundaryTerritoryGroupById(props.boundaryTerritoryGroupId);
  const isBoundaryTerritoryGroupsLoading = useBoundaryTerritoryGroupsIsLoadingSelector();
  const boundaryPermissions = useBoundaryGroupPermissions(props.boundaryGroupId);
  const { boundaryIsCustom, boundaryIsEditable } = boundaryPermissions;
  const { onClose } = props;

  const boundaryStyle = getBoundaryStyle(boundaryTerritoryGroup, boundary?.id);

  const onSubmit = useCallback(async ({ displayName, fillColor }: { displayName: string; fillColor: ColorResult }) => {
    if (!boundary || !boundaryTerritoryGroup) {
      return;
    }

    if (boundaryIsEditable && displayName !== boundary.displayName) {
      try {
        await updateBoundary({
          boundaryId: props.boundaryId,
          boundaryGroupId: props.boundaryGroupId,
          name: displayName,
        });
      }
      catch (e) {
        if (isNotFoundApiError(e)) {
          // boundary was already removed at this point
          onClose();
          dispatch(createAppError({
            type: AppErrorType.General,
            title: t(boundaryIsCustom ? 'Territory was already removed' : 'Boundary was already removed'),
            errorTitle: t(boundaryIsCustom ? 'Territory was already removed from the map' : 'Boundary was already removed from the map'),
          }));
          dispatch(boundaryItemRemoveSuccess(props.boundaryId, BoundaryDeleteResult.AlreadyRemoved));

          return;
        }
      }
    }

    const newStyle = {
      color: guaranteeHash(fillColor.hex),
      opacity: fillColor.rgba.a * 100,
    };

    if (newStyle.color === boundaryStyle.color && newStyle.opacity === boundaryStyle.opacity) {
      onClose();
      return;
    }

    dispatch(boundaryTerritoryGroupsUpdateRequest(
      {
        ...boundaryTerritoryGroup,
        settings: {
          ...boundaryTerritoryGroup.settings,
          boundaryStyle: copy.andReplace(boundaryTerritoryGroup.settings.boundaryStyle, props.boundaryId, newStyle),
        },
      },
      {
        onDone: () => onClose(),
      }
    ));
  }, [boundary, boundaryTerritoryGroup, boundaryIsEditable, boundaryStyle.color, boundaryStyle.opacity, dispatch,
    props.boundaryId, props.boundaryGroupId, updateBoundary, onClose, t, boundaryIsCustom]);

  if (!boundary) {
    return null;
  }

  return (
    <BoundaryItemEditSettingsComponent
      {...props}
      heading={t('Territory Settings')}
      initialFillColor={createColorWithOpacity(boundaryStyle.color, boundaryStyle.opacity / 100)}
      displayName={boundary.displayName}
      onSubmit={onSubmit}
      isLoading={isBoundaryNameSaveLoading || isBoundaryTerritoryGroupsLoading}
      showCreateAnotherButton={false}
      disableNameInput={!boundaryPermissions.boundaryIsEditable}
    />
  );
};
